import type { ReactNode } from "react";
import { PButton } from "./PButton";

// PORT OF `_data_view_lists/paged-data-view` (A `p-dataView` WITH A CUSTOM PAGER):
//   HEADER  sticky, 15px padding; [md:col-2 spacer][col-8 pager][col-4 md:col-2 layout toggle]
//           pager: rounded text chevrons around "Page N of M (X items)", 12.25px bold
//           below 768px and 15.75px from 768px, left-aligned then centred
//   GRID    `grid p-4`, items `col-12 sm:col-6 md:col-4 xl:col-6`
//   LIST    `grid p-4`, rows `col-12 p-0` > `p-1 pl-2 pr-2`, 1px top divider after the first
// `plain` IS THE UNPAGED `_data_view_lists/data-view` INSTEAD (DAILY TASKS): NO PAGER, AND
// `grid grid-nogutter` BODIES - GRID ITEMS WRAPPED `pt-1 pb-2 pr-3 pl-3`, LIST ROWS BARE `col-12`.
export type DataViewLayout = "grid" | "list";

const number = new Intl.NumberFormat("en-US");

export function PagedDataView<T>(props: {
  rows: T[];
  page: number;
  totalPages: number;
  totalRows: number;
  onPage: (page: number) => void;
  layout: DataViewLayout;
  onLayout: (layout: DataViewLayout) => void;
  emptyMessage: string;
  rowKey: (row: T) => string | number;
  render: (row: T, layout: DataViewLayout) => ReactNode;
  plain?: boolean;
  headerStart?: ReactNode;
  headerCenter?: ReactNode;
  headerEnd?: ReactNode;
  gridClassName?: string;
  // REPLACES THE WHOLE HEADER ROW (A TEMPLATE'S OWN LAYOUT), GIVEN THE LIST / GRID TOGGLE TO PLACE
  header?: (toggle: ReactNode) => ReactNode;
}) {
  const { rows, page, totalPages, totalRows, onPage, layout, onLayout, emptyMessage, rowKey, render, plain = false } = props;
  const body = plain ? "grid grid-nogutter" : (props.gridClassName ?? "grid p-4");

  const toggle = (
    // `p-dataViewLayoutOptions`: A TWO-BUTTON SELECT BUTTON (LIST, GRID); THE ACTIVE ONE
    // IN `--primary-700`, THE OTHER IN `--primary-500`. THESE ARE 30PX TALL, NOT 28: THE
    // BUTTONS LACK `p-component`, SO PRIMENG'S HIDDEN `:after { content: "p" }` TAKES THE
    // BROWSER'S BUTTON FONT, WHOSE 16PX LINE OUTGROWS THE 14PX ICON
    <div className="p-inputgroup flex" role="group" aria-label="Layout">
      {(["list", "grid"] as const).map((l) => (
        <PButton
          key={l}
          icon={l === "list" ? "bars" : "th-large"}
          ariaLabel={l === "list" ? "List view" : "Grid view"}
          ariaPressed={layout === l}
          onClick={() => onLayout(l)}
          sx={{
            height: "30px",
            backgroundColor: layout === l ? "var(--primary-700)" : "var(--primary-500)",
            "&:hover": { backgroundColor: layout === l ? "var(--primary-700)" : "var(--primary-600)" },
          }}
        />
      ))}
    </div>
  );

  return (
    <div>
      <div className="pillars-dataview-header">
        {props.header ? (
          props.header(toggle)
        ) : !plain ? (
          <div className="grid grid-nogutter">
            <div className="md:col-2" />
            <div className="col-8 flex flex-row align-items-center justify-content-start md:justify-content-center font-bold text-sm md:text-lg">
              <PButton icon="chevron-left" rounded variant="text" ariaLabel="Previous page" disabled={page <= 1} onClick={() => onPage(page - 1)} />
              <span>
                Page {number.format(page)} of {number.format(totalPages)} <span className="font-bold">({number.format(totalRows)} items)</span>
              </span>
              <PButton icon="chevron-right" rounded variant="text" ariaLabel="Next page" disabled={page >= totalPages} onClick={() => onPage(page + 1)} />
            </div>
            <div className="col-4 md:col-2">
              <div className="flex justify-content-end">{toggle}</div>
            </div>
          </div>
        ) : (
          <div className="flex align-items-center justify-content-between flex-wrap">
            {props.headerStart}
            {props.headerCenter}
            <div className="flex-order-1 flex align-items-center justify-content-end">
              {props.headerEnd}
              {toggle}
            </div>
          </div>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="pillars-dataview-empty">{emptyMessage}</div>
      ) : layout === "grid" ? (
        <div className={body}>
          {rows.map((r) => (
            <div key={rowKey(r)} className="col-12 sm:col-6 md:col-4 xl:col-6">
              <div className={plain ? "pt-1 pb-2 pr-3 pl-3" : undefined}>{render(r, "grid")}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className={body}>
          {rows.map((r, i) =>
            plain ? (
              <div key={rowKey(r)} className="col-12">
                <div>{render(r, "list")}</div>
              </div>
            ) : (
              <div key={rowKey(r)} className="col-12 p-0">
                <div className={`p-1 pl-2 pr-2${i > 0 ? " border-top-1 surface-border" : ""}`}>{render(r, "list")}</div>
              </div>
            ),
          )}
        </div>
      )}
    </div>
  );
}

